import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  FileText,
  Loader2,
  MessageSquare,
  Send,
  Star,
  User,
  X,
} from 'lucide-react';
import { fetchSubmissions, updateSubmissionStatus } from '@/lib/api';
import type { CandidateSubmission } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { formatDate, capitalizeFirst } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface SubmissionsListProps {
  companyId?: string;
  jobId?: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  },
  reviewed: {
    label: 'Reviewed',
    className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  },
  accepted: {
    label: 'Accepted',
    className: 'bg-green-500/20 text-green-400 border-green-500/30',
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-500/20 text-red-400 border-red-500/30',
  },
};

export function SubmissionsList({ companyId, jobId }: SubmissionsListProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['submissions', { companyId, jobId }],
    queryFn: () => fetchSubmissions({ companyId, jobId, limit: 50 }),
  });

  const submissions = data?.data ?? [];

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateSubmissionStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      const statusLabel = STATUS_CONFIG[variables.status]?.label ?? variables.status;
      addToast({
        type: 'success',
        title: `Submission ${statusLabel.toLowerCase()}`,
      });
    },
    onError: (err: Error) => {
      addToast({
        type: 'error',
        title: 'Failed to update submission',
        message: err.message,
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 bg-bg-tertiary rounded-lg border border-white/10">
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full bg-white/5" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48 bg-white/5" />
                <Skeleton className="h-3 w-32 bg-white/5" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full bg-white/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="text-center py-12">
        <Send className="h-12 w-12 mx-auto text-text-tertiary mb-4" />
        <p className="text-text-secondary font-medium">No submissions yet</p>
        <p className="text-sm text-text-tertiary mt-1">
          Submit candidates to companies to track their progress here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {submissions.map((submission: CandidateSubmission) => {
        const statusConfig = STATUS_CONFIG[submission.status] ?? {
          label: submission.status,
          className: 'bg-white/10 text-text-tertiary border-white/10',
        };
        const isPending = submission.status === 'pending';
        const isUpdating = updateStatusMutation.isPending;

        return (
          <div
            key={submission.id}
            className="p-4 bg-bg-tertiary rounded-lg border border-white/10"
          >
            <div className="flex items-start gap-4">
              {/* Candidate avatar */}
              <div className="h-10 w-10 rounded-full bg-bg-elevated flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-text-tertiary" />
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-text-primary">
                    {submission.candidate.firstName} {submission.candidate.lastName}
                  </p>
                  <Badge className={`text-xs border ${statusConfig.className}`}>
                    {statusConfig.label}
                  </Badge>
                </div>

                <div className="mt-1 space-y-0.5">
                  <p className="text-sm text-text-secondary">
                    {submission.job.title}
                    {!jobId && submission.company && (
                      <span className="text-text-tertiary"> at {submission.company.name}</span>
                    )}
                  </p>
                  {submission.candidate.currentJobTitle && (
                    <p className="text-xs text-text-tertiary">
                      Current: {submission.candidate.currentJobTitle}
                    </p>
                  )}
                </div>

                {/* Cover note */}
                {submission.coverNote && (
                  <div className="mt-2 p-2.5 bg-bg-secondary rounded-md border border-white/5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <FileText className="h-3 w-3 text-text-tertiary" />
                      <span className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        Cover Note
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary whitespace-pre-wrap">
                      {submission.coverNote}
                    </p>
                  </div>
                )}

                {/* Portal Feedback */}
                {submission.feedback && submission.feedback.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {submission.feedback.map((fb) => (
                      <div
                        key={fb.id}
                        className="p-2.5 bg-bg-secondary rounded-md border border-white/5"
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <MessageSquare className="h-3 w-3 text-text-tertiary" />
                          <span className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
                            Client Feedback
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          {fb.rating != null && (
                            <div className="flex items-center gap-1">
                              <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
                              <span className="text-sm text-text-primary font-medium">
                                {fb.rating}/5
                              </span>
                            </div>
                          )}
                          {fb.decision && (
                            <Badge
                              className={`text-xs border ${
                                fb.decision === 'accepted'
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                  : fb.decision === 'rejected'
                                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                    : 'bg-white/10 text-text-tertiary border-white/10'
                              }`}
                            >
                              {capitalizeFirst(fb.decision)}
                            </Badge>
                          )}
                        </div>
                        {fb.comments && (
                          <p className="text-sm text-text-secondary whitespace-pre-wrap mt-1.5">
                            {fb.comments}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Meta info */}
                <div className="flex items-center gap-3 mt-2 text-xs text-text-tertiary">
                  <span>Submitted {formatDate(submission.createdAt)}</span>
                  {submission.submittedBy && (
                    <span>
                      by {submission.submittedBy.firstName} {submission.submittedBy.lastName}
                    </span>
                  )}
                </div>
              </div>

              {/* Action buttons for pending submissions */}
              {isPending && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-400 border-green-500/30 hover:bg-green-500/10"
                    disabled={isUpdating}
                    onClick={() =>
                      updateStatusMutation.mutate({
                        id: submission.id,
                        status: 'accepted',
                      })
                    }
                  >
                    {isUpdating ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Accept
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                    disabled={isUpdating}
                    onClick={() =>
                      updateStatusMutation.mutate({
                        id: submission.id,
                        status: 'rejected',
                      })
                    }
                  >
                    {isUpdating ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <X className="h-3.5 w-3.5 mr-1" />
                        Reject
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
